/**
 * Page /admin/whatsapp — connexion WhatsApp via QR (style Sakinea).
 *
 * Workflow :
 *   1. Polling /api/admin/whatsapp/status toutes les 4 secondes pour récupérer
 *      l'état du sidecar (qr / connecting / ready / disconnected / unreachable).
 *   2. Si statut = "qr", on charge le QR PNG via /api/admin/whatsapp/qr.
 *      Le QR se régénère automatiquement côté Baileys ≈ toutes les 20 s.
 *   3. Quand le scan réussit, le statut passe à "ready" et la page bascule
 *      sur l'écran "connecté" (numéro + nom + bouton déconnexion + test).
 *   4. Si le sidecar ne répond pas, on affiche les instructions de lancement.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
    QrCode, Loader2, RefreshCw, CheckCircle2, LogOut, Send,
    AlertTriangle, Zap, Smartphone, Wifi, WifiOff,
} from "lucide-react";

// Polling à 3 secondes : le QR Baileys tourne aux ~20s, et la connexion peut
// flipper rapidement (scan → connecté en <1s). Pattern Sakinea.
const POLL_MS = 3000;

export default function AdminWhatsApp() {
    const [status, setStatus] = useState({ status: "loading" });
    const [qrDataUrl, setQrDataUrl] = useState(null);
    const [loadingAction, setLoadingAction] = useState(null); // "logout" | "test" | null
    const [testTo, setTestTo] = useState("");
    const [refreshing, setRefreshing] = useState(false);
    const pollRef = useRef(null);

    const refreshStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/whatsapp/status");
            setStatus(data);
            // Le sidecar renvoie qr_data_url dans la même réponse que le status
            // (pas besoin de 2e round-trip). Pattern Sakinea.
            if (data.qr_data_url) {
                setQrDataUrl(data.qr_data_url);
            } else if (data.status !== "qr") {
                setQrDataUrl(null);
            }
        } catch (e) {
            setStatus({ status: "unreachable", error: e.message });
        }
    }, []);

    useEffect(() => {
        refreshStatus();
        pollRef.current = setInterval(refreshStatus, POLL_MS);
        return () => clearInterval(pollRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onRefresh = async () => {
        setRefreshing(true);
        await refreshStatus();
        setTimeout(() => setRefreshing(false), 400);
    };

    const onLogout = async () => {
        if (!confirm("Déconnecter la session WhatsApp ? Un nouveau QR sera généré.")) return;
        setLoadingAction("logout");
        try {
            await api.post("/admin/whatsapp/logout");
            toast.success("Déconnexion en cours — un nouveau QR va apparaître");
            setQrDataUrl(null);
            await refreshStatus();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoadingAction(null);
        }
    };

    const onTest = async (e) => {
        e?.preventDefault();
        if (!testTo) {
            toast.error("Saisis un numéro destinataire (format +33...)");
            return;
        }
        setLoadingAction("test");
        try {
            await api.post("/admin/whatsapp/test", { to: testTo });
            toast.success(`Message de test envoyé à ${testTo}`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoadingAction(null);
        }
    };

    const isReady = status.status === "ready";
    const isQrWait = status.status === "qr";
    const isConnecting = status.status === "connecting" || status.status === "loading";
    const isUnreachable = status.status === "unreachable";
    const isDisconnected = status.status === "disconnected";

    return (
        <div className="em-container py-8 max-w-3xl">
            <p className="em-eyebrow">Réglages</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-primary mt-2">
                Connexion WhatsApp
            </h1>
            <p className="text-muted-foreground mt-3">
                Connecte ton compte WhatsApp pour envoyer des notifications depuis ton propre
                numéro quand un visiteur soumet un formulaire.
            </p>

            {/* CARTE PRINCIPALE */}
            <div className="mt-8 rounded-3xl bg-card border border-border overflow-hidden">
                {/* Header de la carte : statut + refresh */}
                <div className="flex items-center justify-between p-5 border-b border-border bg-muted/30">
                    <StatusBadge status={status.status} />
                    <button
                        type="button"
                        onClick={onRefresh}
                        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                        <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                        Rafraîchir
                    </button>
                </div>

                {/* Contenu selon état */}
                <div className="p-8">
                    {isUnreachable && <UnreachableState error={status.error} onRefresh={refreshStatus} />}
                    {isConnecting && <ConnectingState />}
                    {isQrWait && <QrWaitState qrDataUrl={qrDataUrl} />}
                    {isReady && (
                        <ReadyState
                            status={status}
                            testTo={testTo}
                            setTestTo={setTestTo}
                            onTest={onTest}
                            onLogout={onLogout}
                            loadingAction={loadingAction}
                        />
                    )}
                    {isDisconnected && (
                        <div className="text-center py-8 text-muted-foreground">
                            <WifiOff className="h-10 w-10 mx-auto mb-3 opacity-50" />
                            <p>Session déconnectée. Un nouveau QR va apparaître…</p>
                        </div>
                    )}
                </div>
            </div>

            {/* INFO PRATIQUE */}
            <div className="mt-8 rounded-2xl bg-secondary p-6">
                <h3 className="font-display text-lg font-bold flex items-center gap-2 text-primary">
                    <Zap className="h-5 w-5" />
                    Comment ça marche
                </h3>
                <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                    <li className="flex gap-2">
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        Le serveur garde la session WhatsApp active 24/7 (auto-reconnect en cas de coupure).
                    </li>
                    <li className="flex gap-2">
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        Les messages partent depuis <strong>ton</strong> numéro WhatsApp — visible avec ton nom/photo de profil.
                    </li>
                    <li className="flex gap-2">
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        Si tu décroches/changes de téléphone : retourne sur cette page et re-scanne le nouveau QR.
                    </li>
                    <li className="flex gap-2">
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        Le numéro destinataire des notifs est configurable dans <strong>Réglages → Notifications</strong>.
                    </li>
                </ul>
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Sous-composants
// ──────────────────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
    const conf = {
        loading: { color: "bg-muted text-muted-foreground", label: "Chargement…", dot: "bg-muted-foreground" },
        connecting: { color: "bg-amber-100 text-amber-900", label: "Connexion…", dot: "bg-amber-500 animate-pulse" },
        qr: { color: "bg-accent/30 text-primary", label: "En attente du scan", dot: "bg-accent animate-pulse" },
        ready: { color: "bg-emerald-100 text-emerald-900", label: "Connecté", dot: "bg-emerald-500" },
        disconnected: { color: "bg-rose-100 text-rose-900", label: "Déconnecté", dot: "bg-rose-500" },
        unreachable: { color: "bg-rose-100 text-rose-900", label: "Sidecar indisponible", dot: "bg-rose-500" },
    }[status] || { color: "bg-muted text-muted-foreground", label: status || "?", dot: "bg-muted-foreground" };
    return (
        <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${conf.color}`}>
            <span className={`w-2 h-2 rounded-full ${conf.dot}`} />
            {conf.label}
        </span>
    );
}

function UnreachableState({ error, onRefresh }) {
    return (
        <div className="text-center py-6">
            <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
            <h3 className="font-display text-xl font-bold text-primary">Sidecar WhatsApp non lancé</h3>
            <p className="text-muted-foreground mt-2 max-w-md mx-auto text-sm">
                {error || "Le service Node.js qui gère la session WhatsApp ne répond pas."}
            </p>
            <div className="mt-6 bg-muted/40 rounded-xl p-4 max-w-md mx-auto text-left">
                <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2">Lancer le sidecar :</p>
                <pre className="text-xs font-mono bg-background rounded p-3 overflow-x-auto">
{`cd backend/whatsapp_service
npm install      # première fois
npm start`}
                </pre>
            </div>
            <button
                type="button"
                onClick={onRefresh}
                className="inline-flex items-center gap-2 rounded-full mt-6 bg-primary text-primary-foreground px-5 py-2 text-sm font-semibold hover:bg-primary/90"
            >
                <RefreshCw className="h-4 w-4" />
                Re-vérifier
            </button>
        </div>
    );
}

function ConnectingState() {
    return (
        <div className="text-center py-12">
            <Loader2 className="h-12 w-12 mx-auto animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Connexion au sidecar et reprise de la session…</p>
        </div>
    );
}

function QrWaitState({ qrDataUrl }) {
    return (
        <div className="text-center">
            <h3 className="font-display text-2xl font-bold text-primary">Scanne ce QR code</h3>
            <p className="text-muted-foreground mt-2 max-w-md mx-auto text-sm">
                Sur ton téléphone, ouvre <strong>WhatsApp → Réglages → Appareils liés</strong> → <strong>Lier un appareil</strong>, puis vise ce QR.
            </p>
            <div className="mt-7 inline-block p-4 bg-white rounded-2xl ring-4 ring-accent/40">
                {qrDataUrl ? (
                    <img
                        src={qrDataUrl}
                        alt="QR code WhatsApp"
                        className="w-72 h-72 md:w-80 md:h-80"
                    />
                ) : (
                    <div className="w-72 h-72 md:w-80 md:h-80 flex flex-col items-center justify-center text-muted-foreground bg-muted/30 rounded-xl">
                        <QrCode className="h-12 w-12 opacity-40 mb-2" />
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <p className="text-xs mt-2">Génération du QR…</p>
                    </div>
                )}
            </div>
            <p className="text-xs text-muted-foreground mt-5">
                Le QR se régénère automatiquement toutes les ~20 secondes.
                La page se met à jour seule dès que tu scannes.
            </p>
        </div>
    );
}

function ReadyState({ status, testTo, setTestTo, onTest, onLogout, loadingAction }) {
    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4 p-5 rounded-2xl bg-emerald-50 border-2 border-emerald-200">
                <div className="h-14 w-14 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="h-8 w-8 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-emerald-900 uppercase tracking-wider">Connecté</p>
                    <p className="font-display text-2xl font-black text-emerald-950 truncate">
                        {status.name || "Compte WhatsApp"}
                    </p>
                    {status.phone && (
                        <p className="text-sm text-emerald-800 flex items-center gap-1.5 mt-0.5">
                            <Smartphone className="h-3.5 w-3.5" />
                            {status.phone}
                        </p>
                    )}
                </div>
                <button
                    type="button"
                    onClick={onLogout}
                    disabled={loadingAction === "logout"}
                    className="inline-flex items-center gap-2 rounded-full bg-white border border-emerald-300 px-4 py-2 text-sm font-semibold text-emerald-900 hover:bg-emerald-100 disabled:opacity-50"
                >
                    {loadingAction === "logout" ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <LogOut className="h-4 w-4" />
                    )}
                    Déconnecter
                </button>
            </div>

            {/* Envoi de test */}
            <form onSubmit={onTest} className="rounded-2xl border border-border bg-muted/20 p-5 space-y-3">
                <div className="flex items-center gap-2 text-sm font-bold text-primary">
                    <Send className="h-4 w-4" />
                    Envoyer un message de test
                </div>
                <p className="text-xs text-muted-foreground">
                    Le destinataire reçoit immédiatement un message confirmant la connexion.
                </p>
                <div className="flex gap-2">
                    <input
                        type="tel"
                        value={testTo}
                        onChange={(e) => setTestTo(e.target.value)}
                        placeholder="+33612345678"
                        className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono"
                    />
                    <button
                        type="submit"
                        disabled={loadingAction === "test" || !testTo}
                        className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-5 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50"
                    >
                        {loadingAction === "test" ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Send className="h-4 w-4" />
                        )}
                        Envoyer
                    </button>
                </div>
            </form>

            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Wifi className="h-3.5 w-3.5" />
                Connexion maintenue automatiquement par le sidecar. Auto-reconnect en cas de coupure réseau.
            </div>
        </div>
    );
}
